// The pages of the application and who may open them (GEN-01, GEN-17; frozen P0 mockup SCR-FND-03).
// One source for the router, the side menu and the bottom bar. The database still refuses every operation
// a role may not run: hiding a page is never the protection.
import type { IconName } from '../design/icons';

export type Role = 'owner' | 'seller' | 'storekeeper';
export const roles: readonly Role[] = ['owner', 'seller', 'storekeeper'];

export type PageGroup = 'g.daily' | 'g.stock' | 'g.biz' | 'g.admin';

export interface PageDef {
  id: string;
  path: string;
  icon: IconName;
  group: PageGroup;
  /** Backlog phase that builds the page; the shell shows a placeholder until then. */
  phase: string;
  /** Pages driven by a truck show the context strip (GEN-14). */
  truck?: boolean;
}

export const PAGES: readonly PageDef[] = [
  { id: 'dashboard', path: '/dashboard', icon: 'grid', group: 'g.daily', phase: 'P11' },
  { id: 'round', path: '/round', icon: 'route', group: 'g.daily', phase: 'P5', truck: true },
  { id: 'sales', path: '/sales', icon: 'swap', group: 'g.daily', phase: 'P6' },
  { id: 'returns', path: '/returns', icon: 'return', group: 'g.daily', phase: 'P8', truck: true },
  { id: 'orders', path: '/orders', icon: 'box', group: 'g.daily', phase: 'P9', truck: true },
  { id: 'inventory', path: '/inventory', icon: 'layers', group: 'g.stock', phase: 'P2', truck: true },
  { id: 'purchasing', path: '/purchasing', icon: 'cart', group: 'g.stock', phase: 'P4' },
  { id: 'products', path: '/products', icon: 'tag', group: 'g.stock', phase: 'P1' },
  { id: 'suppliers', path: '/suppliers', icon: 'truck', group: 'g.stock', phase: 'P1' },
  { id: 'customers', path: '/customers', icon: 'users', group: 'g.biz', phase: 'P1' },
  { id: 'trucks', path: '/trucks', icon: 'truck', group: 'g.biz', phase: 'P5' },
  { id: 'promotions', path: '/promotions', icon: 'tag', group: 'g.biz', phase: 'P6' },
  { id: 'expenses', path: '/expenses', icon: 'file', group: 'g.biz', phase: 'P7' },
  { id: 'sellers', path: '/sellers', icon: 'users', group: 'g.biz', phase: 'P7' },
  { id: 'agreements', path: '/agreements', icon: 'handshake', group: 'g.biz', phase: 'P7' },
  { id: 'capital', path: '/capital', icon: 'cash', group: 'g.biz', phase: 'P3' },
  { id: 'reports', path: '/reports', icon: 'chart', group: 'g.biz', phase: 'P11' },
  { id: 'settings', path: '/settings', icon: 'gear', group: 'g.admin', phase: 'P0' },
];

/** Pages each role may open. Seller: no Expenses page until DEC-26 is decided. */
export const ROLE_PAGES: Record<Role, readonly string[]> = {
  owner: PAGES.map((p) => p.id),
  seller: ['round', 'sales', 'returns', 'orders', 'inventory', 'customers'],
  storekeeper: ['inventory', 'purchasing', 'products', 'suppliers', 'orders', 'trucks'],
};

/** The four pages of the phone bottom bar per role; "more" opens the full menu. */
export const BOTTOM_PAGES: Record<Role, readonly string[]> = {
  owner: ['dashboard', 'round', 'sales', 'inventory'],
  seller: ['round', 'sales', 'returns', 'orders'],
  storekeeper: ['inventory', 'purchasing', 'orders', 'trucks'],
};

export const GROUPS: readonly PageGroup[] = ['g.daily', 'g.stock', 'g.biz', 'g.admin'];

export function pageById(id: string): PageDef | undefined {
  return PAGES.find((p) => p.id === id);
}

export function pageByPath(path: string): PageDef | undefined {
  return PAGES.find((p) => p.path === path);
}

export function canOpen(role: Role, pageId: string): boolean {
  return ROLE_PAGES[role].includes(pageId);
}

/** Where a role lands after sign-in: the first page of its bottom bar. */
export function homeOf(role: Role): PageDef {
  const page = pageById(BOTTOM_PAGES[role][0] ?? 'settings');
  if (!page) throw new Error(`No home page for ${role}`);
  return page;
}
